import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { MatLegacyChipInputEvent as MatChipInputEvent } from '@angular/material/legacy-chips';
import { UntypedFormControl } from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  catchError,
  debounceTime,
  finalize,
  map,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import {
  EntityInfoService,
  GroupingEntity,
} from 'src/app/shared/services/metadata-api/entity-info.service';
import { SearchService } from 'src/app/shared/services/metadata-api/search.service';
import { AiSearchService } from 'src/app/shared/services/ai-search/ai-search.service';
import {
  CompanyQueryRequest,
  EntityEnrichmentRequest,
  EnrichmentResult,
} from 'src/app/shared/models/ai-search.interface';
import { mapEnrichmentToFormValues } from './enrichment-mapper';

export interface CompanyFormData {
  request: CompanyQueryRequest;
  entityRequestData: Record<string, unknown>[];
}

interface SearchEntity {
  type: string;
  alpha_id: string;
  name: string;
  country?: { alpha_id: string; name: string };
}

interface SearchHit {
  entity: SearchEntity;
  count: number | null;
}

interface FreeTextOption {
  _freeText: string;
}

type CompanySearchOption = SearchHit | FreeTextOption;

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-company-form',
  templateUrl: './company-form.component.html',
  styleUrls: ['./company-form.component.scss'],
})
export class CompanyFormComponent implements OnInit, OnDestroy {
  @Input() initialLegalName = '';
  @Input() autoSetAsNewCompany = false;
  @Input() userEmail = '';
  @Input() project = '';
  @Output() validityChange = new EventEmitter<boolean>();
  @Output() submitForm = new EventEmitter<CompanyFormData>();
  @Output() navigateToEntity = new EventEmitter<{
    type: string;
    id: string;
  }>();
  @Output() companyStepChange = new EventEmitter<'search' | 'form'>();
  @Output() searchStateChange = new EventEmitter<{
    enabled: boolean;
    enriching: boolean;
  }>();

  companyStep: 'search' | 'form' = 'search';
  enriching = false;
  enrichmentError = '';

  // Search step
  legalNameControl = new UntypedFormControl('');
  companySearchResults$: Observable<SearchHit[]>;
  selectedEntity: SearchEntity | null = null;
  isNewCompany = false;

  // Form fields
  legalName = '';
  website = '';
  city = '';
  isin = '';
  ceoName = '';
  surfaceForms: string[] = [];
  message = '';

  readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  sectorControl = new UntypedFormControl('');
  countryControl = new UntypedFormControl('');

  sectors: GroupingEntity[] = [];
  countries: GroupingEntity[] = [];

  filteredSectors: Observable<GroupingEntity[]>;
  filteredCountries: Observable<GroupingEntity[]>;

  private destroy$ = new Subject<void>();

  constructor(
    private entityInfoService: EntityInfoService,
    private searchService: SearchService,
    private aiSearchService: AiSearchService,
  ) {}

  ngOnInit(): void {
    if (this.initialLegalName) {
      this.legalNameControl.setValue(this.initialLegalName);
      if (this.autoSetAsNewCompany) {
        this.isNewCompany = true;
        this.legalName = this.initialLegalName;
        this.countryControl.enable();
        setTimeout(() => this.emitSearchState());
      }
    }
    this.setupCompanySearch();
    this.loadSectors();
    this.loadCountries();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- Display functions ---

  displayCompanySearch = (
    option: CompanySearchOption | string | null,
  ): string => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    if ('_freeText' in option) return option._freeText;
    return option.entity?.name || '';
  };

  displayGroupingEntity(entity: GroupingEntity | string): string {
    if (!entity) return '';
    if (typeof entity === 'string') return entity;
    return entity.name || '';
  }

  // --- Getters ---

  get selectedCountry(): GroupingEntity | null {
    const val = this.countryControl.value;
    return val && typeof val === 'object' ? val : null;
  }

  get selectedSector(): GroupingEntity | null {
    const val = this.sectorControl.value;
    return val && typeof val === 'object' ? val : null;
  }

  get legalNameText(): string {
    return this.displayCompanySearch(this.legalNameControl.value);
  }

  get isSearchEnabled(): boolean {
    if (this.enriching) return false;
    if (this.legalNameText.trim().length === 0) return false;
    if (this.selectedEntity) return true;
    return this.website.trim().length > 0 && !!this.selectedCountry;
  }

  get isValid(): boolean {
    return (
      this.legalName.trim().length > 0 &&
      !!this.selectedSector &&
      !!this.selectedCountry
    );
  }

  // --- Search step handlers ---

  emitSearchState(): void {
    this.searchStateChange.emit({
      enabled: this.isSearchEnabled,
      enriching: this.enriching,
    });
  }

  onCompanySearchSelect(option: CompanySearchOption): void {
    if ('_freeText' in option) {
      this.selectedEntity = null;
      this.isNewCompany = true;
      this.legalName = option._freeText;
      this.countryControl.enable();
      // Overwrite Material's object-valued selection with a plain string so
      // subsequent typing reports string values through valueChanges.
      setTimeout(() => this.legalNameControl.setValue(this.legalName));
    } else {
      this.selectedEntity = option.entity;
      this.isNewCompany = false;
      this.legalName = option.entity.name;
      this.countryControl.disable();
    }
    this.emitSearchState();
  }

  onLegalNameInput(): void {
    this.selectedEntity = null;
    this.isNewCompany = false;
    this.countryControl.enable();
    this.emitSearchState();
  }

  onSearch(): void {
    if (!this.isSearchEnabled) return;

    if (this.selectedEntity) {
      this.navigateToEntity.emit({
        type: this.selectedEntity.type,
        id: this.selectedEntity.alpha_id,
      });
      return;
    }

    this.legalName = this.legalNameText;
    this.enriching = true;
    this.enrichmentError = '';
    this.legalNameControl.disable();
    this.emitSearchState();

    const request: EntityEnrichmentRequest = {
      companies: [
        {
          name: this.legalName.trim(),
          country: this.selectedCountry?.alpha_id?.toUpperCase() || undefined,
          website: this.website.trim() || undefined,
        },
      ],
      skip_llm: false,
    };

    this.aiSearchService
      .generateEntityEnrichment(request)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.enriching = false;
          this.legalNameControl.enable();
          this.emitSearchState();
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.results?.length > 0) {
            this.applyEnrichmentValues(response.results[0]);
          }
          this.goToFormStep();
        },
        error: (err) => {
          this.enrichmentError =
            err?.error?.detail || 'Enrichment search failed. Please try again.';
        },
      });
  }

  // --- Form step handlers ---

  emitValidity(): void {
    this.validityChange.emit(this.isValid);
  }

  onSubmit(): void {
    if (!this.isValid) return;

    const request: CompanyQueryRequest = {
      legal_name: this.legalName.trim(),
      sector: this.selectedSector!.name!,
      country: this.selectedCountry!.name!,
      company_type: 'public',
    };
    if (this.website.trim()) request.website = this.website.trim();
    if (this.city.trim()) request.city = this.city.trim();
    if (this.isin.trim()) request.isin = this.isin.trim();
    if (this.ceoName.trim()) request.ceo_name = this.ceoName.trim();
    if (this.surfaceForms.length > 0) request.surface_forms = this.surfaceForms;
    if (this.message.trim()) request.message = this.message.trim();

    const entityRequestData = [
      {
        legal_name: this.legalName.trim(),
        additional_information: {
          time: Date.now(),
          email: this.userEmail,
          project: this.project,
          message: this.message.trim(),
          sector: this.selectedSector?.name || '',
          website: this.website.trim(),
          surfaceForms: this.surfaceForms.join(),
          city: this.city.trim(),
          country: this.selectedCountry?.name || '',
          isin: this.isin.trim(),
          ceo: this.ceoName.trim(),
        },
      },
    ];

    this.submitForm.emit({ request, entityRequestData });
  }

  skipToForm(): void {
    this.legalName = this.legalNameText;
    this.enrichmentError = '';
    this.goToFormStep();
  }

  goBackToSearch(): void {
    this.companyStep = 'search';
    this.companyStepChange.emit('search');
    this.clearEnrichmentFields();
    this.validityChange.emit(false);
    this.emitSearchState();
  }

  private goToFormStep(): void {
    // Country is always editable in the form step regardless of what happened
    // in the search step (existing entity selection disables it there).
    this.countryControl.enable();
    this.companyStep = 'form';
    this.companyStepChange.emit('form');
    this.emitValidity();
  }

  addSurfaceForm(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      this.surfaceForms.push(value);
    }
    event.chipInput.clear();
  }

  removeSurfaceForm(form: string): void {
    const index = this.surfaceForms.indexOf(form);
    if (index >= 0) {
      this.surfaceForms.splice(index, 1);
    }
  }

  // --- Private ---

  private setupCompanySearch(): void {
    this.companySearchResults$ = this.legalNameControl.valueChanges.pipe(
      debounceTime(300),
      switchMap((value) => {
        // Only search when user is typing (string value).
        // Object values mean something was selected — don't re-search.
        if (typeof value !== 'string' || value.length < 2) return of([]);
        return this.searchService.search(value, false).pipe(
          map((res: { hits?: SearchHit[] }) =>
            (res.hits || []).filter((hit) => hit.entity?.type === 'company'),
          ),
          catchError(() => of([] as SearchHit[])),
        );
      }),
    );
  }

  private applyEnrichmentValues(result: EnrichmentResult): void {
    const values = mapEnrichmentToFormValues(result);
    if (values.sectorAlphaId) {
      const match = this.sectors.find(
        (s) => s.alpha_id === values.sectorAlphaId,
      );
      if (match) this.sectorControl.setValue(match);
    }
    if (values.countryAlphaId && !this.selectedCountry) {
      const match = this.countries.find(
        (c) => c.alpha_id === values.countryAlphaId,
      );
      if (match) this.countryControl.setValue(match);
    }
    if (values.city) this.city = values.city;
    if (values.isin) this.isin = values.isin;
    if (values.ceoName) this.ceoName = values.ceoName;
    if (values.surfaceForms.length > 0) {
      this.surfaceForms = [...values.surfaceForms];
    }
  }

  private clearEnrichmentFields(): void {
    this.sectorControl.setValue('');
    this.city = '';
    this.isin = '';
    this.ceoName = '';
    this.surfaceForms = [];
    this.message = '';
  }

  private loadSectors(): void {
    this.entityInfoService.getSectors().subscribe((res) => {
      this.sectors = res.entities;

      this.filteredSectors = this.sectorControl.valueChanges.pipe(
        startWith(''),
        map((value) => {
          const filterText =
            typeof value === 'string' ? value : value?.name || '';
          return filterText
            ? this.filterEntities(this.sectors, filterText)
            : this.sectors.slice();
        }),
      );
    });
  }

  private loadCountries(): void {
    this.entityInfoService.getCountries().subscribe((res) => {
      this.countries = res.entities;

      this.filteredCountries = this.countryControl.valueChanges.pipe(
        startWith(''),
        map((value) => {
          const filterText =
            typeof value === 'string' ? value : value?.name || '';
          return filterText
            ? this.filterEntities(this.countries, filterText)
            : this.countries.slice();
        }),
      );
    });
  }

  private filterEntities(
    entities: GroupingEntity[],
    value: string,
  ): GroupingEntity[] {
    const filterValue = value.toLowerCase().trim();
    if (!filterValue) return entities.slice();
    return entities
      .filter((e) => e.name?.toLowerCase().includes(filterValue))
      .sort((a, b) => {
        const aName = a.name?.toLowerCase() || '';
        const bName = b.name?.toLowerCase() || '';
        const aStarts = aName.startsWith(filterValue);
        const bStarts = bName.startsWith(filterValue);
        if (aStarts !== bStarts) return aStarts ? -1 : 1;
        const aWordStart = aName
          .split(' ')
          .some((w) => w.startsWith(filterValue));
        const bWordStart = bName
          .split(' ')
          .some((w) => w.startsWith(filterValue));
        if (aWordStart !== bWordStart) return aWordStart ? -1 : 1;
        return (a.name || '').localeCompare(b.name || '');
      });
  }
}
